import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import API from '../services/api';
import { formatDate, formatDateLong } from '../dateUtils';
import { getTeamMoments } from '../teamMoments';
import { GiveShoutOutModal, SHOUT_GLOW_CSS } from './ShoutOutComponents';

const VIBE_OPTIONS = [
  { key: 'sunny', emoji: '⚡', label: 'Energized' },
  { key: 'mostly_sunny', emoji: '🙂', label: 'Good' },
  { key: 'cloudy', emoji: '😐', label: 'Okay' },
  { key: 'rainy', emoji: '😮‍💨', label: 'Drained' },
  { key: 'stormy', emoji: '😫', label: 'Exhausted' },
];

const ROLE_LABEL = { OFFICE_ADMIN: 'Office Admin', PORTAL_ADMIN: 'Portal Admin', THERAPIST: 'Therapist' };
const isAdminRole = (role) => role === 'OFFICE_ADMIN' || role === 'PORTAL_ADMIN';

// TeamDirectory shows all therapists and lets you click through to see a richer profile.
// It filters by search term and optionally by "accepting new clients".
// Clicking on a profile opens a modal with birthday (day+month only), hire date,
// years at practice, license, specialties, education, bio, interests, and other details.
// The design uses subtle gradients, badges, and grid layouts for a polished look.
function TeamDirectory() {
  const [teamProfiles, setTeamProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAccepting, setFilterAccepting] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Format a date string as "MMM d" (e.g., "Sep 10"). Returns null if invalid.
  // Parses safely to avoid UTC-midnight timezone shift.
  const formatMonthDay = (dateStr) => {
    if (!dateStr) return null;
    const datePart = String(dateStr).split('T')[0];
    const parts = datePart.split('-').map(Number);
    if (parts.length < 3) return null;
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    if (isNaN(d)) return null;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  // Format a date string as "MMM d, yyyy". Returns null if invalid.
  // Uses formatDateLong from dateUtils for safe parsing.
  const formatFullDate = (dateStr) => formatDateLong(dateStr) || null;

  // Calculate the number of full years since the given date (hire anniversary). Returns null if invalid.
  const yearsSince = (dateStr, now = new Date()) => {
    if (!dateStr) return null;
    const dateOnly = String(dateStr).split('T')[0].split('-').map(Number);
    if (dateOnly.length < 3 || dateOnly.some((n) => Number.isNaN(n))) return null;
    const d = new Date(dateOnly[0], dateOnly[1] - 1, dateOnly[2]);
    if (isNaN(d)) return null;
    let years = now.getFullYear() - d.getFullYear();
    const hadAnniv =
      now.getMonth() > d.getMonth() ||
      (now.getMonth() === d.getMonth() && now.getDate() >= d.getDate());
    if (!hadAnniv) years -= 1;
    return Math.max(years, 0);
  };

  // Fetch team profiles on mount
  useEffect(() => {
    const fetchTeamProfiles = async () => {
      try {
        setLoading(true);
        const response = await API.get('/therapist/team-profiles');
        setTeamProfiles(response.data || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching team profiles:', err);
        setError('Unable to load team profiles. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchTeamProfiles();
  }, []);

  // Filter and search profiles with memoization
  const filteredProfiles = useMemo(() => {
    const term = (searchTerm || '').toLowerCase();
    return (teamProfiles || []).filter((p) => {
      const fullName = `${p.first_name} ${p.last_name}`.toLowerCase();
      const searchMatch =
        !term ||
        fullName.includes(term) ||
        (p.specialties && p.specialties.toLowerCase().includes(term)) ||
        (p.license_type && p.license_type.toLowerCase().includes(term));
      const acceptingMatch = !filterAccepting || (!isAdminRole(p.role) && !!p.accepting_clients);
      return searchMatch && acceptingMatch;
    });
  }, [teamProfiles, searchTerm, filterAccepting]);

  // Upcoming birthdays/anniversaries for the Team Pulse band (whole team, not the filtered list)
  const teamMoments = useMemo(
    () => getTeamMoments(teamProfiles, { windowDays: 31 }),
    [teamProfiles]
  );
  const acceptingCount = useMemo(
    () => (teamProfiles || []).filter((p) => !isAdminRole(p.role) && p.accepting_clients).length,
    [teamProfiles]
  );

  const currentUserId = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').id; } catch (_) { return null; }
  })();
  const myRole = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').role; } catch (_) { return null; } })();
  const backDashPath = isAdminRole(myRole) ? '/admin/dashboard' : '/therapist/dashboard';
  const [shoutOuts, setShoutOuts] = useState([]);
  const [shoutModalOpen, setShoutModalOpen] = useState(false);

  const fetchShoutOuts = () => {
    API.get('/therapist/shout-outs')
      .then((r) => setShoutOuts(r.data.items || []))
      .catch(() => { /* non-fatal */ });
  };

  useEffect(() => {
    fetchShoutOuts();
    API.post('/therapist/shout-outs/mark-seen').catch(() => {});
  }, []);

  const deleteShoutOut = (id) => {
    API.delete(`/therapist/shout-outs/${id}`).then(fetchShoutOuts).catch(() => {});
  };

  const [prompt, setPrompt] = useState(null);
  const [promptAnswers, setPromptAnswers] = useState([]);
  const [myAnswer, setMyAnswer] = useState('');
  const [answerDraft, setAnswerDraft] = useState('');
  const [editingAnswer, setEditingAnswer] = useState(false);
  const [suggestText, setSuggestText] = useState('');
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [revealedId, setRevealedId] = useState(null);
  const [hoveredCardId, setHoveredCardId] = useState(null);

  const fetchPrompt = () => {
    API.get('/therapist/team-prompt')
      .then((r) => {
        setPrompt(r.data.prompt || null);
        setPromptAnswers(r.data.answers || []);
        setMyAnswer(r.data.myAnswer || '');
        setAnswerDraft(r.data.myAnswer || '');
      })
      .catch(() => {});
  };

  useEffect(() => { fetchPrompt(); }, []);

  const answersByUser = useMemo(() => {
    const m = {};
    for (const a of promptAnswers) m[String(a.userId)] = a.answer;
    return m;
  }, [promptAnswers]);

  const submitAnswer = () => {
    const text = answerDraft.trim();
    if (!text) return;
    API.post('/therapist/team-prompt/answer', { answer: text })
      .then(() => { setEditingAnswer(false); fetchPrompt(); })
      .catch(() => {});
  };

  const submitSuggestion = () => {
    const text = suggestText.trim();
    if (!text) return;
    API.post('/therapist/team-prompt/suggest', { text })
      .then(() => { setSuggestText(''); setSuggestOpen(false); })
      .catch(() => {});
  };

  const [vibe, setVibe] = useState({ myWeather: null, count: 0, aggregate: null, distribution: null });

  const fetchVibe = () => {
    API.get('/therapist/vibe-check')
      .then((r) => setVibe(r.data || { myWeather: null, count: 0, aggregate: null, distribution: null }))
      .catch(() => {});
  };

  useEffect(() => { fetchVibe(); }, []);

  const setMyVibe = (weather) => {
    API.post('/therapist/vibe-check', { weather }).then(fetchVibe).catch(() => {});
  };

  // Open/close modal handlers
  const openProfileDetail = (profile) => {
    setSelectedProfile(profile);
    setShowDetailModal(true);
  };
  const closeProfileDetail = () => {
    setShowDetailModal(false);
    setSelectedProfile(null);
  };

  return (
    <div style={containerStyle}>
      <style>{SHOUT_GLOW_CSS}</style>
      <div style={heroStyle}>
        <div style={heroInnerStyle}>
          <div>
            <div style={heroEyebrowStyle}>Avenues Unlimited Counseling Center</div>
            <h1 style={heroTitleStyle}>Team Hub</h1>
            <div style={heroAccentStyle} />
          </div>
          <Link to={backDashPath} style={backChipStyle}>← Dashboard</Link>
        </div>
      </div>

      <div style={searchFilterContainerStyle}>
        <div style={searchContainerStyle}>
          <input
            type="text"
            placeholder="Search by name, specialty, or license..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
        </div>
        <div style={filterContainerStyle}>
          <label style={filterLabelStyle}>
            <input
              type="checkbox"
              checked={filterAccepting}
              onChange={() => setFilterAccepting(!filterAccepting)}
            />
            <span style={filterTextStyle}>Show only therapists accepting new clients</span>
          </label>
        </div>
      </div>

      {!loading && !error && teamProfiles.length > 0 && (
        <div style={pulseBandStyle}>
          <div style={pulseSummaryStyle}>
            <div style={{ fontSize: 26 }}>👥</div>
            <div>
              <div style={pulseSummaryNumStyle}>
                {teamProfiles.length} teammate{teamProfiles.length === 1 ? '' : 's'}
              </div>
              <div style={pulseSummarySubStyle}>{acceptingCount} accepting new clients</div>
            </div>
          </div>
          <div style={pulseCelebrationsStyle}>
            <div style={pulseLabelStyle}>Coming up this month</div>
            {teamMoments.length === 0 ? (
              <span style={{ color: '#6b7280', fontSize: 13 }}>No birthdays or anniversaries coming up</span>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {teamMoments.map((mo) => (
                  <span key={`${mo.type}-${mo.profile.therapist_id}`} style={pulsePillStyle}>
                    {mo.type === 'birthday' ? '🎂' : '🎉'} {mo.profile.first_name}
                    {' · '}
                    {mo.type === 'anniversary'
                      ? `${mo.years} yr${mo.years === 1 ? '' : 's'}`
                      : mo.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && !error && teamProfiles.length > 0 && (
        <div style={promptWidgetStyle}>
          {prompt ? (
            <>
              <div style={promptQStyle}>✨ This week: <strong>{prompt.text}</strong></div>
              {(!editingAnswer && myAnswer) ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
                  <span style={{ fontSize: 14 }}>Your answer: <strong>{myAnswer}</strong></span>
                  <button style={promptLinkBtnStyle} onClick={() => { setAnswerDraft(myAnswer); setEditingAnswer(true); }}>Edit</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
                  <input value={answerDraft} maxLength={140} onChange={(e) => setAnswerDraft(e.target.value)} placeholder="Add your answer…" style={promptInputStyle} />
                  <button style={promptBtnStyle} onClick={submitAnswer} disabled={!answerDraft.trim()}>Save</button>
                  {myAnswer && <button style={promptLinkBtnStyle} onClick={() => { setEditingAnswer(false); setAnswerDraft(myAnswer); }}>Cancel</button>}
                </div>
              )}
              <div style={promptHintStyle}>Hover a teammate (or tap 💬 on a card) to see their answer.</div>
            </>
          ) : (
            <div style={promptQStyle}>✨ Weekly prompt — none yet. Suggest one to kick things off!</div>
          )}
          <div style={{ marginTop: 8 }}>
            <button style={promptLinkBtnStyle} onClick={() => setSuggestOpen((v) => !v)}>💡 Suggest a prompt</button>
            {suggestOpen && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <input value={suggestText} maxLength={140} onChange={(e) => setSuggestText(e.target.value)} placeholder="Your prompt idea…" style={promptInputStyle} />
                <button style={promptBtnStyle} onClick={submitSuggestion} disabled={!suggestText.trim()}>Submit</button>
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && !error && teamProfiles.length > 0 && (
        <div style={vibeWidgetStyle}>
          <div style={vibeTitleStyle}>🌡️ This week's vibe</div>
          <div style={vibeButtonsStyle}>
            {VIBE_OPTIONS.map((o) => (
              <button
                key={o.key}
                title={o.label}
                onClick={() => setMyVibe(o.key)}
                style={{ ...vibeBtnStyle, ...(vibe.myWeather === o.key ? vibeBtnActiveStyle : {}) }}
              >
                <span style={{ fontSize: 22 }}>{o.emoji}</span>
              </button>
            ))}
          </div>
          {vibe.aggregate ? (
            <div style={vibeAggStyle}>
              Team energy: <strong>{vibe.aggregate.label}</strong> · {vibe.count} checked in
              <div style={vibeTallyStyle}>
                {VIBE_OPTIONS.map((o) => (
                  <span key={o.key} style={{ marginRight: 10 }}>
                    {o.emoji} {vibe.distribution ? vibe.distribution[o.key] : 0}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div style={vibeHintStyle}>Anonymous · {vibe.count}/3 check-ins to reveal the team energy.</div>
          )}
        </div>
      )}

      {!loading && !error && (
        <div style={shoutPanelStyle}>
          <div style={shoutHeaderStyle}>
            <h3 style={shoutTitleStyle}>💜 Shout-Outs</h3>
            <button style={shoutGiveBtnStyle} onClick={() => setShoutModalOpen(true)}>Give a shout-out</button>
          </div>
          {shoutOuts.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>
              No shout-outs yet — be the first to brighten someone's day.
            </p>
          ) : (
            <div style={shoutFeedStyle}>
              {shoutOuts.map((s) => (
                <div key={s.id} style={shoutItemStyle}>
                  <span className="shout-glow" style={shoutItemEmojiStyle}>{s.emoji || '💜'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13 }}>
                      <strong>{s.fromName}</strong> → <strong>{s.toName}</strong>
                      {s.visibility === 'private' && <span title="Private — just them" style={{ marginLeft: 6 }}>🔒</span>}
                    </div>
                    <div style={{ fontSize: 14, margin: '2px 0', color: '#374151' }}>“{s.message}”</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{new Date(s.createdAt).toLocaleString()}</div>
                  </div>
                  {s.mine && (
                    <button onClick={() => deleteShoutOut(s.id)} title="Delete" style={shoutDeleteBtnStyle}>×</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div style={loadingStyle}>Loading team profiles...</div>
      ) : error ? (
        <div style={errorStyle}>{error}</div>
      ) : filteredProfiles.length === 0 ? (
        <div style={noResultsStyle}>No therapists match your search criteria.</div>
      ) : (
        <div style={profilesGridStyle}>
          {filteredProfiles.map((profile) => {
            const tenure = yearsSince(profile.hire_date);
            return (
              <div
                key={profile.therapist_id}
                style={{ ...profileCardStyle, ...(hoveredCardId === profile.therapist_id ? profileCardHoverStyle : {}) }}
                onClick={() => openProfileDetail(profile)}
                onMouseEnter={() => { setHoveredCardId(profile.therapist_id); if (answersByUser[String(profile.therapist_id)]) setRevealedId(profile.therapist_id); }}
                onMouseLeave={() => { setHoveredCardId(null); setRevealedId((id) => (id === profile.therapist_id ? null : id)); }}
              >
                <div style={profileCardTopStyle}>
                  <div style={profilePhotoContainerStyle}>
                    {profile.photo_url ? (
                      <img
                        src={profile.photo_url}
                        alt={`${profile.first_name} ${profile.last_name}`}
                        style={profilePhotoStyle}
                      />
                    ) : (
                      <div
                        style={{
                          ...profilePhotoStyle,
                          backgroundColor: '#e0e0e0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.3rem',
                          color: '#666',
                        }}
                      >
                        {(profile.first_name?.[0] || '') + (profile.last_name?.[0] || '')}
                      </div>
                    )}
                  </div>
                  <div style={profileCardHeaderStyle}>
                    <h3 style={profileNameStyle}>
                      {profile.first_name} {profile.last_name}
                    </h3>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {isAdminRole(profile.role) ? (
                        <span style={licenseTypeStyle}>{profile.job_title || ROLE_LABEL[profile.role]}</span>
                      ) : (
                        profile.license_type && <span style={licenseTypeStyle}>{profile.license_type}</span>
                      )}
                      {typeof tenure === 'number' && (
                        <span style={tenureBadgeStyle}>
                          {tenure === 0
                            ? 'First year'
                            : `${tenure} yr${tenure === 1 ? '' : 's'}`} at practice
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {!isAdminRole(profile.role) && profile.specialties && (
                  <p style={specialtiesStyle}>
                    <strong>Specialties:</strong> {profile.specialties}
                  </p>
                )}

                {!isAdminRole(profile.role) && (
                  <div style={acceptingClientsContainerStyle}>
                    <div
                      style={{
                        ...acceptingClientsIndicatorStyle,
                        backgroundColor: profile.accepting_clients ? '#10B981' : '#EF4444',
                      }}
                    />
                    <span style={acceptingClientsTextStyle}>
                      {profile.accepting_clients
                        ? 'Accepting new clients'
                        : 'Not accepting new clients'}
                    </span>
                  </div>
                )}

                {answersByUser[String(profile.therapist_id)] && (
                  <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
                    <button
                      style={revealChipStyle}
                      onClick={() => setRevealedId((id) => (id === profile.therapist_id ? null : profile.therapist_id))}
                    >
                      💬 {revealedId === profile.therapist_id ? 'Hide answer' : 'See answer'}
                    </button>
                    {revealedId === profile.therapist_id && (
                      <div style={answerStripStyle}>
                        {prompt && <div style={answerQStyle}>{prompt.text}</div>}
                        {answersByUser[String(profile.therapist_id)]}
                      </div>
                    )}
                  </div>
                )}

                <div style={viewProfileBtnContainerStyle}>
                  <button style={viewProfileBtnStyle}>View Profile</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Profile Detail Modal */}
      {showDetailModal && selectedProfile && (
        <div style={modalOverlayStyle} onClick={closeProfileDetail}>
          <div
            style={modalContentStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={modalHeaderStyle}>
              <h2 style={modalTitleStyle}>
                {selectedProfile.first_name} {selectedProfile.last_name}
                {isAdminRole(selectedProfile.role)
                  ? <span style={modalLicenseBadgeStyle}>{selectedProfile.job_title || ROLE_LABEL[selectedProfile.role]}</span>
                  : (selectedProfile.license_type && <span style={modalLicenseBadgeStyle}>{selectedProfile.license_type}</span>)}
              </h2>
              <button
                onClick={closeProfileDetail}
                style={modalCloseButtonStyle}
              >
                &times;
              </button>
            </div>

            {/* decorative hero stripe */}
            <div style={modalHeroStyle} />

            <div style={modalBodyStyle}>
              <div style={modalProfileHeaderStyle}>
                <div style={modalPhotoContainerStyle}>
                  {selectedProfile.photo_url ? (
                    <img
                      src={selectedProfile.photo_url}
                      alt={`${selectedProfile.first_name} ${selectedProfile.last_name}`}
                      style={modalPhotoStyle}
                    />
                  ) : (
                    <div
                      style={{
                        ...modalPhotoStyle,
                        backgroundColor: '#e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '2rem',
                        color: '#666',
                      }}
                    >
                      {(selectedProfile.first_name?.[0] || '') +
                        (selectedProfile.last_name?.[0] || '')}
                    </div>
                  )}
                </div>

                <div style={modalProfileInfoStyle}>
                  {!isAdminRole(selectedProfile.role) && (
                    <div style={acceptingClientsContainerStyle}>
                      <div
                        style={{
                          ...acceptingClientsIndicatorStyle,
                          width: 12,
                          height: 12,
                          backgroundColor: selectedProfile.accepting_clients
                            ? '#10B981'
                            : '#EF4444',
                        }}
                      />
                      <span
                        style={{
                          ...acceptingClientsTextStyle,
                          fontSize: '1rem',
                        }}
                      >
                        {selectedProfile.accepting_clients
                          ? 'Accepting new clients'
                          : 'Not accepting new clients'}
                      </span>
                    </div>
                  )}

                  {/* Quick facts row: birthday, hire date, tenure */}
                  <div style={quickFactsRowStyle}>
                    <Fact
                      label="Birthday"
                      value={formatMonthDay(selectedProfile.dob) || '—'}
                    />
                    <Fact
                      label="Hire date"
                      value={formatFullDate(selectedProfile.hire_date) || '—'}
                    />
                    <Fact
                      label="Years at practice"
                      value={
                        yearsSince(selectedProfile.hire_date) ?? '—'
                      }
                    />
                  </div>

                  {/* Emergency contact info */}
                  <div style={{ marginTop: 12 }}>
                    <h4 style={{ margin: '0 0 6px', fontSize: '1rem' }}>Emergency Contact</h4>
                    <div style={emergencyBoxStyle}>
                      <div>
                        <strong>Name:</strong> {selectedProfile.emergency_contact_name || '—'}
                      </div>
                      <div>
                        <strong>Phone:</strong> {selectedProfile.emergency_contact_phone || '—'}
                      </div>
                    </div>
                  </div>

                  {/* Unavailable reason if applicable */}
                  {selectedProfile.unavailable_reason && !selectedProfile.accepting_clients && (
                    <div style={unavailableReasonStyle}>
                      <p style={{ margin: 0 }}>
                        <strong>Reason:</strong> {selectedProfile.unavailable_reason}
                      </p>
                    </div>
                  )}

                  {!isAdminRole(selectedProfile.role) && selectedProfile.license_number && (
                    <p style={detailItemStyle}>
                      <strong>License #:</strong> {selectedProfile.license_number}
                    </p>
                  )}
                </div>
              </div>

              <div style={modalDetailSectionStyle}>
                {!isAdminRole(selectedProfile.role) && selectedProfile.specialties && (
                  <Section title="Specialties">
                    {selectedProfile.specialties}
                  </Section>
                )}
                {!isAdminRole(selectedProfile.role) && selectedProfile.client_demographics && (
                  <Section title="Client Demographics">
                    {selectedProfile.client_demographics}
                  </Section>
                )}
                {selectedProfile.education && (
                  <Section title="Education">
                    {selectedProfile.education}
                  </Section>
                )}
                {selectedProfile.bio && (
                  <Section title="About">
                    {selectedProfile.bio}
                  </Section>
                )}
                {selectedProfile.interests && (
                  <Section title="Personal Interests">
                    {selectedProfile.interests}
                  </Section>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <GiveShoutOutModal
        isOpen={shoutModalOpen}
        onClose={() => setShoutModalOpen(false)}
        teammates={teamProfiles.filter((p) => String(p.therapist_id) !== String(currentUserId))}
        onPosted={fetchShoutOuts}
      />
    </div>
  );
}

// Presentational helper for detail sections
function Section({ title, children }) {
  return (
    <div style={modalSectionStyle}>
      <h3 style={sectionTitleStyle}>{title}</h3>
      <p style={{ marginTop: 8 }}>{children}</p>
    </div>
  );
}

// Presentational helper for quick facts cells
function Fact({ label, value }) {
  return (
    <div style={factStyle}>
      <div style={factLabelStyle}>{label}</div>
      <div style={factValueStyle}>{String(value)}</div>
    </div>
  );
}

/* ---------------- Styles ---------------- */
const containerStyle = {
  padding: '2rem',
  maxWidth: 1200,
  margin: '0 auto',
};
const heroStyle = {
  background: 'linear-gradient(135deg, #1D2C5F 0%, #16224A 100%)',
  borderRadius: 16,
  padding: '26px 30px',
  marginBottom: '1.5rem',
  boxShadow: '0 12px 32px rgba(29,44,95,0.20)',
};
const heroInnerStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
  gap: 16, flexWrap: 'wrap',
};
const heroEyebrowStyle = {
  color: '#EDE9E0', fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase',
};
const heroTitleStyle = { color: '#fff', margin: '6px 0 0', fontSize: 30, fontWeight: 800, letterSpacing: -0.5 };
const heroAccentStyle = { width: 56, height: 4, borderRadius: 2, background: '#C67A52', marginTop: 12 };
const backChipStyle = {
  textDecoration: 'none', color: '#fff', background: 'rgba(255,255,255,0.12)',
  border: '1px solid rgba(255,255,255,0.28)', borderRadius: 999, padding: '8px 18px',
  fontWeight: 600, fontSize: 14,
};
const searchFilterContainerStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: '0.75rem',
  marginBottom: '1.25rem',
};
const searchContainerStyle = { flex: 1 };
const searchInputStyle = {
  width: '100%',
  padding: '0.7rem 0.9rem',
  borderRadius: 10,
  border: '1px solid #dbe2f0',
  fontSize: '1rem',
  background: '#fff',
  boxShadow: '0 1px 2px rgba(29,44,95,0.04)',
  boxSizing: 'border-box',
};
const filterContainerStyle = { display: 'flex', alignItems: 'center' };
const filterLabelStyle = { display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 6 };
const filterTextStyle = { fontSize: '0.95rem' };

const loadingStyle = {
  textAlign: 'center',
  padding: '2rem',
  fontSize: '1.1rem',
  color: '#6b7280',
};
const errorStyle = {
  textAlign: 'center',
  padding: '1rem',
  fontSize: '1rem',
  color: '#b91c1c',
  background: '#fee2e2',
  borderRadius: 8,
};
const noResultsStyle = {
  textAlign: 'center',
  padding: '1.25rem',
  fontSize: '1rem',
  color: '#6b7280',
  background: '#f3f4f6',
  borderRadius: 8,
};

const profilesGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: '1.25rem',
};
const profileCardStyle = {
  padding: '1.1rem',
  borderRadius: 14,
  boxShadow: '0 4px 16px rgba(29,44,95,0.06)',
  border: '1px solid #e6e8ef',
  background: '#fff',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform .18s ease, box-shadow .18s ease',
};
const profileCardHoverStyle = {
  transform: 'translateY(-3px)',
  boxShadow: '0 14px 30px rgba(29,44,95,0.14)',
};
const profileCardTopStyle = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '0.75rem',
};
const profilePhotoContainerStyle = { marginRight: '0.75rem' };
const profilePhotoStyle = {
  width: 56,
  height: 56,
  borderRadius: '50%',
  objectFit: 'cover',
  border: '2.5px solid #1D2C5F',
};
const profileCardHeaderStyle = { flex: 1 };
const profileNameStyle = { margin: 0, marginBottom: 4, fontSize: '1.05rem', fontWeight: 700 };
const licenseTypeStyle = {
  display: 'inline-block',
  fontSize: '0.75rem',
  background: '#eef1f7',
  color: '#1D2C5F',
  padding: '2px 9px',
  borderRadius: 999,
  fontWeight: 600,
};
const tenureBadgeStyle = {
  display: 'inline-block',
  fontSize: '0.75rem',
  background: '#f8efe8',
  color: '#9a5a36',
  padding: '2px 9px',
  borderRadius: 999,
  fontWeight: 600,
};
const specialtiesStyle = {
  margin: '0.35rem 0',
  fontSize: '0.9rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
};
const acceptingClientsContainerStyle = { display: 'flex', alignItems: 'center', marginTop: 6, gap: 8 };
const acceptingClientsIndicatorStyle = { width: 8, height: 8, borderRadius: '999px' };
const acceptingClientsTextStyle = { fontSize: '0.85rem', fontWeight: 600, color: '#374151' };
const viewProfileBtnContainerStyle = { marginTop: '0.75rem' };
const viewProfileBtnStyle = {
  background: '#1D2C5F',
  color: '#fff',
  border: 'none',
  padding: '0.55rem 0.75rem',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: 600,
  width: '100%',
};

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};
const modalContentStyle = {
  background: 'white',
  borderRadius: 12,
  width: 'min(940px, 94vw)',
  maxHeight: '92vh',
  overflow: 'auto',
  boxShadow: '0 12px 40px rgba(0,0,0,.25)',
};
const modalHeaderStyle = {
  padding: '1rem 1.25rem',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};
const modalHeroStyle = { height: 12, background: 'linear-gradient(90deg, #1D2C5F, #C67A52)' };
const modalTitleStyle = { margin: 0, fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: 10, color: '#1D2C5F' };
const modalLicenseBadgeStyle = { fontSize: '0.8rem', background: '#eef1f7', color: '#1D2C5F', padding: '2px 9px', borderRadius: 999, fontWeight: 600 };
const modalCloseButtonStyle = { background: 'none', border: 'none', fontSize: '1.6rem', cursor: 'pointer', color: '#6b7280' };

const modalBodyStyle = { padding: '1.25rem' };
const modalProfileHeaderStyle = { display: 'flex', gap: 16, marginBottom: '1.25rem', alignItems: 'center' };
const modalPhotoContainerStyle = {};
const modalPhotoStyle = { width: 112, height: 112, borderRadius: '50%', objectFit: 'cover', border: '3px solid #1D2C5F' };
const modalProfileInfoStyle = { flex: 1 };

const quickFactsRowStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 10 };
const factStyle = { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px' };
const factLabelStyle = { fontSize: '0.75rem', color: '#6b7280' };
const factValueStyle = { fontSize: '0.95rem', fontWeight: 600, color: '#111827' };

// Emergency contact box styling
const emergencyBoxStyle = {
  background: '#f8fafc',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 10,
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 8,
};

const unavailableReasonStyle = { background: '#fff7ed', border: '1px solid #fed7aa', padding: 10, borderRadius: 8, marginTop: 8, fontSize: '0.9rem' };
const detailItemStyle = { margin: '0.4rem 0', fontSize: '0.95rem' };

const modalDetailSectionStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 };
const modalSectionStyle = { marginBottom: 8 };
const sectionTitleStyle = { borderBottom: '2px solid #1D2C5F', paddingBottom: 6, marginBottom: 8, fontSize: '1.05rem', color: '#1D2C5F' };

// Shared widget-card chrome (unified across pulse / prompt / vibe / shout-outs).
const widgetCard = {
  background: '#fff',
  border: '1px solid #e6e8ef',
  borderRadius: 14,
  padding: '16px 20px',
  marginBottom: '1.25rem',
  boxShadow: '0 4px 16px rgba(29,44,95,0.06)',
};
const ACCENT_NAVY = '4px solid #1D2C5F';
const ACCENT_TERRA = '4px solid #C67A52';

const pulseBandStyle = {
  ...widgetCard,
  borderLeft: ACCENT_NAVY,
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
  alignItems: 'center',
};
const pulseSummaryStyle = { display: 'flex', alignItems: 'center', gap: 12, paddingRight: 16 };
const pulseSummaryNumStyle = { fontSize: '1.1rem', fontWeight: 700, color: '#1D2C5F' };
const pulseSummarySubStyle = { fontSize: '0.85rem', color: '#6b7280' };
const pulseCelebrationsStyle = {
  flex: 1,
  minWidth: 220,
  borderLeft: '1px solid #e6e8ef',
  paddingLeft: 16,
};
const pulseLabelStyle = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: '#9aa1b1',
  marginBottom: 6,
};
const pulsePillStyle = {
  display: 'inline-block',
  background: '#eef1f7',
  color: '#1D2C5F',
  borderRadius: 999,
  padding: '4px 11px',
  fontSize: '0.85rem',
  fontWeight: 600,
};

const shoutPanelStyle = { ...widgetCard, borderLeft: ACCENT_TERRA };
const shoutHeaderStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 10, flexWrap: 'wrap',
};
const shoutTitleStyle = { margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#1D2C5F' };
const shoutGiveBtnStyle = {
  background: '#1D2C5F', color: '#fff', border: 'none', borderRadius: 8,
  padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 14,
};
const shoutFeedStyle = { display: 'flex', flexDirection: 'column', gap: 10 };
const shoutItemStyle = {
  display: 'flex', alignItems: 'flex-start', gap: 10,
  background: '#f7f8fb', border: '1px solid #eceef4', borderRadius: 10, padding: '10px 12px',
};
const shoutDeleteBtnStyle = {
  background: 'none', border: 'none', color: '#9ca3af', fontSize: 18, cursor: 'pointer', lineHeight: 1, flexShrink: 0,
};
const shoutItemEmojiStyle = { fontSize: 22, lineHeight: 1, flexShrink: 0, marginTop: 1 };

const promptWidgetStyle = { ...widgetCard, borderLeft: ACCENT_TERRA };
const promptQStyle = { fontSize: '1rem', color: '#1D2C5F' };
const promptInputStyle = {
  flex: 1, minWidth: 180, padding: '8px 10px', border: '1px solid #dbe2f0',
  borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
};
const promptBtnStyle = {
  background: '#C67A52', color: '#fff', border: 'none', borderRadius: 8,
  padding: '8px 14px', fontWeight: 600, cursor: 'pointer',
};
const promptLinkBtnStyle = {
  background: 'none', border: 'none', color: '#C67A52', fontWeight: 600,
  cursor: 'pointer', fontSize: 13, padding: 0, textDecoration: 'underline',
};
const promptHintStyle = { marginTop: 8, fontSize: 12, color: '#9aa1b1' };

const revealChipStyle = {
  background: '#f8efe8', color: '#9a5a36', border: '1px solid #ecd9cb',
  borderRadius: 999, padding: '3px 11px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
};
const answerStripStyle = {
  marginTop: 8, background: '#f8efe8', border: '1px solid #ecd9cb',
  borderRadius: 8, padding: '8px 10px', fontSize: 13, color: '#5c3a25',
};
const answerQStyle = {
  fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6,
  color: '#9a5a36', fontWeight: 700, marginBottom: 2,
};

const vibeWidgetStyle = { ...widgetCard, borderLeft: ACCENT_NAVY };
const vibeTitleStyle = { fontSize: '1rem', fontWeight: 700, color: '#1D2C5F', marginBottom: 10 };
const vibeButtonsStyle = { display: 'flex', gap: 8, flexWrap: 'wrap' };
const vibeBtnStyle = {
  background: '#fff', border: '1px solid #dbe2f0', borderRadius: 10,
  padding: '6px 12px', cursor: 'pointer', lineHeight: 1,
};
const vibeBtnActiveStyle = { border: '2px solid #1D2C5F', background: '#eef1f7' };
const vibeAggStyle = { marginTop: 10, fontSize: 14, color: '#1D2C5F' };
const vibeTallyStyle = { marginTop: 4, fontSize: 13, color: '#475569' };
const vibeHintStyle = { marginTop: 10, fontSize: 13, color: '#6b7280' };

export default TeamDirectory;